import React, { useEffect, useMemo } from 'react'
import { useSelector } from 'react-redux'
import { CategoryResponse, TypesenseProduct } from './types'
import { ProductCard } from '../products/components/ProductCard'
import { SearchResponse } from 'typesense/lib/Typesense/Documents'
import ContactCard from '../../components/ContactCard'
import Pagination, { PaginationCounter, PaginationDetails } from '../../components/Pagination'
import { PAGINATION_DATA_LIMIT, SortingOptions } from '../../util/consts'
import ProductList from '../products/components/ProductList'
import ProductListContainer from '../products/pages/ProductsListContainer'
import {
  DigitalFarmerAccount,
  RelationshipOwner,
  selectCurrentFarmerAccount,
} from '../azure-b2c/storage'
import { ActivePrice, Product } from '../products/storage'
import TestIds from '../../testIds'
import { SelectedFiltersType } from '../products/components/ProductFilter'
import { getCategories, multiSearchWithActivePrices } from './api'
import { AllProductsCover } from '../products/pages/AllProductsCover'
import { isBulkQuotable } from '../products/utils/isBulkQuotable'
import RequestBulkHelperMessage from '../quotes/components/HelperMessage/RequestBulkHelperMessage'
import { Box, Typography } from '@mui/material'
import { Loader, LoadingBox } from '../../components/Loader'
import { debounce } from 'lodash'
import { generateImageUrl } from '../../util/helpers'
import { usePreviousPage } from '../../util/usePreviousPage'
import { selectCurrentCart } from '../cart/storage/cart.slice'
import { StocksSlice } from '../products/storage/stocks.slice'
import { useAppDispatch } from '../../store/rootStore'
import { CartDeliveryMode } from '../cart/storage/cart.types'
import HelperMessage from '../../components/HelperMessage'
import { selectStores } from '../stores/storage/stores.slice'
import { colors } from '../../themes/colors'
import { locationToSiteId } from '../stores/storage/utils'

export const sortOptions = [
  SortingOptions.BestMatch,
  SortingOptions.PriceAscending,
  SortingOptions.PriceDescending,
]

interface TypesenseSearchProps extends Record<string, unknown> {
  accountNumber: string | undefined
  setCategoriesCallback: (categories: string[]) => void
  paginationCounter: React.ReactNode
  status: string
  data: Product[]
  handleClearFilters: () => void
  isFilterApplied: boolean
  sortBy: string
  setSortBy: React.Dispatch<React.SetStateAction<string>>
  paginatedData: Product[]
  selectedFilters: SelectedFiltersType
  selectedAccount: DigitalFarmerAccount | undefined
  currentPage: number
  relationshipOwner: RelationshipOwner
}

const InnerTypesenseSearch: React.FC<TypesenseSearchProps> = ({
  accountNumber,
  setCategoriesCallback,
  data,
  handleClearFilters,
  isFilterApplied,
  sortBy,
  selectedFilters,
  selectedAccount,
  currentPage,
  relationshipOwner,
  setSortBy,
}) => {
  const dispatch = useAppDispatch()
  const [hits, setHits] = React.useState<SearchResponse<TypesenseProduct & ActivePrice>>()
  const [categories, setCategories] = React.useState<CategoryResponse[]>([])
  const [showCoverPage, setShowCoverPage] = React.useState<boolean>(true)
  const [loading, setLoading] = React.useState<boolean>(true)
  const cart = useSelector(selectCurrentCart)
  const stores = useSelector(selectStores)

  const showInStockMessageHelper = React.useMemo(() => {
    return cart?.cartDeliveryMode === CartDeliveryMode.DELIVERY && selectedFilters.inStock.value
  }, [selectedFilters.inStock.value, cart?.cartDeliveryMode])

  const account = useSelector(selectCurrentFarmerAccount)
  const isTfo = account?.hasTfoAccess

  useEffect(() => {
    if (categories) {
      setCategoriesCallback(categories.map((e) => e.value))
    }
  }, [categories])

  useEffect(() => {
    if (isFilterApplied) {
      setShowCoverPage(false)
    }
  }, [isFilterApplied])

  useEffect(() => {
    const element = document.getElementById(TestIds.pages.mainScrollContainer)
    if (element) element.scrollTo({ top: 0, behavior: 'auto' })
  }, [showCoverPage])

  const { previousPage } = usePreviousPage()
  const restoreScrollPosition = () => {
    setTimeout(() => {
      const element = document.getElementById(TestIds.pages.mainScrollContainer)
      if (
        element &&
        sessionStorage.scrollYPositionOnProducts &&
        previousPage.startsWith('/products/')
      ) {
        element.scrollTo({
          top: parseInt(sessionStorage.scrollYPositionOnProducts) ?? 0,
          behavior: 'auto',
        })
      }
    }, 100)
  }

  useEffect(() => {
    if (!loading) restoreScrollPosition()
  }, [loading])

  const paginationCounter = useMemo(() => {
    const count = hits?.found ?? 0
    const total = hits?.found
    const currentPage = hits?.page ?? 0

    return (
      <PaginationCounter
        data-testid={TestIds.components.pagination.number}
        label='products'
        itemsPerPage={PAGINATION_DATA_LIMIT}
        count={count}
        total={total}
        currentPage={currentPage}
      />
    )
  }, [hits, currentPage, selectedFilters, isFilterApplied])

  const pagination = useMemo(() => {
    const total = hits?.found
    const _currentPage = hits?.page ?? currentPage

    if (hits && total && total > PAGINATION_DATA_LIMIT) {
      return (
        <Pagination
          path='/products'
          page={_currentPage}
          count={Math.ceil(total / PAGINATION_DATA_LIMIT)}
        />
      )
    }
  }, [hits])

  useEffect(() => {
    ;(async () => {
      const { categories } = await getCategories()
      const filteredCategories = categories.filter((x) => {
        if (x.value.startsWith('Seed')) {
          return true
        }
        return true
      })
      setCategories(filteredCategories)
      if (!sortOptions.includes(sortBy as SortingOptions)) {
        setSortBy(sortOptions[0])
      }
    })()
  }, [])

  const fetchResults = async (
    selectedFilters: SelectedFiltersType,
    sortBy: string,
    currentPage: number,
  ) => {
    try {
      setLoading(true)
      // multiSearchWithActivePrice if you want to wait for results in one go
      const branchId = locationToSiteId(selectedFilters.storeId.value ?? '')
      const { searchResults } = await multiSearchWithActivePrices({
        accountNumber,
        currentPage,
        sortBy,
        selectedFilters,
        branchId,
      })
      setHits(searchResults)
      dispatch(
        StocksSlice.actions.addSearchResults(
          searchResults.hits?.map((hit) => {
            return { ...hit.document, lifeCycleState: hit.document.lifecycle }
          }),
        ),
      )
      setLoading(false)
    } catch (error) {
      console.error('Error fetching results:', error)
    }
  }

  const debouncedFetchResults = React.useCallback(
    debounce((selectedFilters, sortBy, currentPage) => {
      fetchResults(selectedFilters, sortBy, currentPage)
    }, 100),
    [
      selectedFilters.category.value,
      selectedFilters.searchText.value,
      selectedFilters.dateRange.value,
      selectedFilters.priceRange.value,
      selectedFilters.inStock.value,
      selectedFilters.storeId.value,
      sortBy,
      currentPage,
    ],
  )

  useEffect(() => {
    debouncedFetchResults(selectedFilters, sortBy, currentPage)
  }, [debouncedFetchResults])

  const productsToRender = React.useMemo(() => {
    if (hits) {
      const hitsToRender = hits
      return hitsToRender?.hits?.map((hit) => hit.document)
    }
  }, [hits])

  if (showCoverPage) return <AllProductsCover selectedFilters={selectedFilters} />
  if (!hits && !loading) {
    return null
  }
  if (loading)
    return (
      <LoadingBox>
        <Loader
          title='Loading your products'
          type='inline-flex'
          sx={{ marginTop: '12px' }}
          data-testid={TestIds.components.loader.searchProduct}
        />
      </LoadingBox>
    )

  return (
    <ProductListContainer>
      <PaginationDetails
        onClear={handleClearFilters}
        showClear={isFilterApplied}
        paginationCounter={paginationCounter}
        sorting={{
          value: sortBy,
          onChange: setSortBy,
          options: sortOptions,
        }}
      />
      <HelperMessage
        show={
          !!isTfo &&
          showInStockMessageHelper &&
          stores.find((e) => e.inventorySiteId === account?.homeBranchId) !== undefined
        }
        body={
          <Typography variant='body2' color={colors.primary800}>
            <b>You currently have Delivery selected</b>
            <br />
            {`Stock levels shown are for the home branch: \n ${stores.find((e) => e.inventorySiteId === account?.homeBranchId)?.orgUnitName}`}
            <br />
            Products can still be ordered in or fulfilled from other stores.
          </Typography>
        }
      />
      {productsToRender && (
        <ProductList<TypesenseProduct & ActivePrice>
          items={productsToRender}
          renderListItem={(e) => {
            const hit = e
            const match = data.find((e) => e.productId === hit.id, 10)

            const showRequestBulkHelperMessage = () => {
              return (isBulkQuotable(selectedFilters.category.value.split('>')[0]) &&
                e.price_negotiable &&
                selectedAccount?.relationshipOwner?.email &&
                isBulkQuotable(e.l1_category) &&
                productsToRender.indexOf(e) <=
                  productsToRender.findIndex(
                    (x) => isBulkQuotable(x.l1_category) && x.price_negotiable,
                  )) as boolean
            }
            return (
              <Box key={e.productId}>
                <RequestBulkHelperMessage show={showRequestBulkHelperMessage()} />
                <ProductCard
                  key={hit.id}
                  isPastProduct={false}
                  item={{
                    category: hit.l1_category,
                    subCategory: hit.l2_category,
                    name: hit.name,
                    description: hit.name,
                    lifeCycleState: hit.lifecycle,
                    masterProductId: hit.id,
                    productId: hit.productId,
                    orders: [...(match?.orders ?? [])],
                    itemNumber: hit.item_id,
                    imageUrl: generateImageUrl(hit.item_id),
                    basePriceExcGst: hit.basePriceExcGst,
                    basePriceIncGst: hit.price,
                    discountedPriceExcGst: hit.discountedPriceExcGst,
                    discountedPriceIncGst: hit.discountedPriceIncGst,
                    hasVariants: hit.has_variants,
                    isPriceNegotiable: hit.price_negotiable,
                    stockAvailabilities: hit.stockAvailabilities,
                    stockAvailability: hit.stock,
                  }}
                  enableQuote={!!selectedAccount?.relationshipOwner?.fullName}
                />
              </Box>
            )
          }}
        />
      )}
      {pagination}
      {hits && hits.found < 25 && (
        <ContactCard
          title={`Can't find what you're looking for?\nContact your Farmlands representative.`}
          sx={{ minWidth: '100%' }}
          {...relationshipOwner}
        />
      )}
    </ProductListContainer>
  )
}

const TypesenseSearch: React.FC<TypesenseSearchProps> = (props) => {
  return <InnerTypesenseSearch {...props} />
}

export default TypesenseSearch
